import { API_BASE } from "../config";
import React, { useState, useEffect } from 'react';

// Diet data with categories, names, and descriptions
const DIET_CATEGORIES = {
  'Medical & Therapeutic Diets': [
    {
      name: 'Low FODMAP',
      description: 'Eliminates fermentable carbohydrates that can trigger IBS symptoms. Reduces bloating, gas, and digestive discomfort by avoiding certain fruits, vegetables, grains, and dairy products.'
    },
    {
      name: 'Low Residue',
      description: 'Limits high-fiber foods to reduce bowel movement frequency and volume. Often prescribed before medical procedures or for inflammatory bowel conditions.'
    },
    {
      name: 'Gluten-Free',
      description: 'Eliminates gluten protein found in wheat, barley, and rye. Essential for celiac disease management and may help with non-celiac gluten sensitivity.'
    },
    {
      name: 'Ketogenic (medical epilepsy protocol)',
      description: 'Very high-fat, very low-carbohydrate diet used medically to reduce seizure frequency in epilepsy patients. Requires strict medical supervision and monitoring.'
    },
    {
      name: 'Diabetic Diet',
      description: 'Controls carbohydrate intake and emphasizes balanced nutrition to manage blood sugar levels. Focuses on consistent meal timing and portion control.'
    },
    {
      name: 'Renal Diet',
      description: 'Restricts protein, phosphorus, potassium, and sodium to reduce kidney workload. Tailored to specific stages of kidney disease and individual lab values.'
    },
    {
      name: 'DASH (Dietary Approaches to Stop Hypertension)',
      description: 'Emphasizes fruits, vegetables, whole grains, and lean proteins while limiting sodium, saturated fat, and added sugars to lower blood pressure.'
    },
    {
      name: 'Heart Healthy / Cardiac Diet',
      description: 'Reduces saturated and trans fats, cholesterol, and sodium while emphasizing omega-3 fatty acids, fiber, and antioxidants to support cardiovascular health.'
    },
    {
      name: 'Liver-Friendly Diet',
      description: 'Supports liver function by limiting alcohol, processed foods, and excess fats. Emphasizes antioxidant-rich foods and adequate protein for liver repair.'
    },
    {
      name: 'PKU (Phenylketonuria) Diet',
      description: 'Severely restricts phenylalanine intake through specialized low-protein foods and medical formulas. Essential for preventing intellectual disability in PKU patients.'
    },
    {
      name: 'Low Sodium Diet',
      description: 'Limits sodium intake to manage high blood pressure, heart failure, or kidney disease. Emphasizes fresh foods and herbs for flavoring instead of salt.'
    },
    {
      name: 'Anti-Reflux / GERD Diet',
      description: 'Avoids trigger foods like citrus, tomatoes, spicy foods, and caffeine that can worsen acid reflux. Emphasizes smaller, more frequent meals.'
    },
    {
      name: 'Elimination Diet (allergen testing)',
      description: 'Systematically removes and reintroduces foods to identify allergies or intolerances. Requires careful monitoring and professional guidance.'
    },
    {
      name: 'BRAT Diet (bananas, rice, applesauce, toast)',
      description: 'Temporary diet of bland, low-fiber foods used to manage diarrhea and digestive upset. Not nutritionally complete for long-term use.'
    },
    {
      name: 'GAPS Diet (Gut and Psychology Syndrome)',
      description: 'Eliminates grains, processed foods, and certain carbohydrates to heal digestive issues. Claims to address neurological and psychological conditions through gut health.'
    },
    {
      name: 'SCD (Specific Carbohydrate Diet)',
      description: 'Restricts complex carbohydrates and processed foods to manage inflammatory bowel disease. Allows only simple sugars that are easily absorbed.'
    }
  ],
  'Popular & Weight-Loss Diets': [
    {
      name: 'Atkins',
      description: 'Low-carbohydrate diet that progresses through phases, starting with very low carbs and gradually increasing. Emphasizes protein and fat for weight loss.'
    },
    {
      name: 'South Beach',
      description: 'Three-phase diet that eliminates refined carbs and sugars initially, then gradually reintroduces good carbs. Focuses on lean proteins and healthy fats.'
    },
    {
      name: 'Zone Diet',
      description: 'Maintains a specific ratio of carbohydrates, proteins, and fats (40:30:30) at each meal to control inflammation and optimize hormonal balance.'
    },
    {
      name: 'Dukan Diet',
      description: 'High-protein, low-carbohydrate diet with four phases. Starts with pure protein days and gradually adds vegetables and other foods back.'
    },
    {
      name: 'Weight Watchers (WW)',
      description: 'Point-based system that assigns values to foods based on calories, saturated fat, sugar, and protein. Emphasizes portion control and lifestyle changes.'
    },
    {
      name: 'Nutrisystem',
      description: 'Pre-packaged meal delivery program that controls portions and calories. Provides structured eating plan with convenient, shelf-stable meals.'
    },
    {
      name: 'SlimFast',
      description: 'Meal replacement program using shakes, bars, and snacks. Typically replaces two meals daily with products and includes one balanced meal.'
    },
    {
      name: 'HMR Diet',
      description: 'Health Management Resources program using meal replacements and structured eating. Emphasizes behavior modification and medical supervision.'
    },
    {
      name: 'Medifast',
      description: 'Low-calorie meal replacement program with medical supervision. Uses proprietary products to create ketosis for rapid weight loss.'
    },
    {
      name: 'Volumetrics Diet',
      description: 'Focuses on eating foods with low energy density (high water/fiber content) to feel full on fewer calories. Emphasizes fruits, vegetables, and soups.'
    },
    {
      name: 'Jenny Craig',
      description: 'Structured program combining pre-packaged meals with personal coaching. Gradually transitions to self-prepared meals following program principles.'
    },
    {
      name: 'Raw Food Diet',
      description: 'Consists primarily of uncooked, unprocessed plant foods. Believes cooking destroys beneficial enzymes and nutrients in food.'
    },
    {
      name: 'Meal Replacement Shakes / Shakes-Based Diets',
      description: 'Uses protein shakes or meal replacement drinks to control calories and nutrition. Convenient option for busy lifestyles but may lack dietary variety.'
    }
  ],
  'Low-Carb / High-Fat Approaches': [
    {
      name: 'Keto (general lifestyle version)',
      description: 'Very low-carbohydrate, high-fat diet that induces ketosis for weight loss and potential health benefits. Less strict than medical ketogenic protocols.'
    },
    {
      name: 'Modified Keto (Lazy / Dirty Keto, Targeted Keto, Cyclical Keto)',
      description: 'Variations of ketogenic diet with different approaches to carb restriction, timing, or food quality. More flexible than standard keto protocols.'
    },
    {
      name: 'Paleo',
      description: 'Based on presumed ancient human diet, emphasizing meat, fish, vegetables, fruits, and nuts while avoiding grains, legumes, and dairy.'
    },
    {
      name: 'Primal',
      description: 'Similar to Paleo but allows some dairy and focuses on lifestyle factors beyond diet. Emphasizes whole foods and traditional preparation methods.'
    },
    {
      name: 'Carnivore / Zero Carb',
      description: 'Consists exclusively of animal products - meat, fish, eggs, and sometimes dairy. Eliminates all plant foods and carbohydrates.'
    },
    {
      name: 'Ancestral Diets',
      description: 'Based on traditional eating patterns of various cultures before industrialization. Emphasizes whole foods and traditional preparation methods.'
    }
  ],
  'Plant-Based Diets': [
    {
      name: 'Vegan',
      description: 'Excludes all animal products including meat, dairy, eggs, and honey. Relies entirely on plant-based foods for nutrition.'
    },
    {
      name: 'Vegetarian (Lacto, Ovo, Lacto-Ovo)',
      description: 'Excludes meat but may include dairy (lacto), eggs (ovo), or both (lacto-ovo). Various levels of plant-based eating with some animal products.'
    },
    {
      name: 'Pescatarian',
      description: 'Plant-based diet that includes fish and seafood but excludes other meat. Often includes dairy and eggs as well.'
    },
    {
      name: 'Flexitarian / Semi-Vegetarian',
      description: 'Primarily plant-based diet with occasional meat consumption. Flexible approach that reduces but doesn\'t eliminate animal products.'
    },
    {
      name: 'Raw Vegan',
      description: 'Combines vegan principles with raw food philosophy. All foods consumed are plant-based and uncooked below 104-118°F.'
    },
    {
      name: 'Fruitarian',
      description: 'Subsists primarily on fruits, nuts, and seeds. May include vegetables that are botanically fruits like tomatoes and peppers.'
    },
    {
      name: 'Whole-Food Plant-Based (WFPB)',
      description: 'Emphasizes minimally processed plant foods while avoiding animal products and processed foods, including refined oils and sugars.'
    },
    {
      name: 'Macrobiotic',
      description: 'Based on Eastern philosophy, emphasizing whole grains, vegetables, and traditional preparation methods. Seeks balance between yin and yang foods.'
    }
  ],
  'Cultural & Traditional Diets': [
    {
      name: 'Mediterranean Diet',
      description: 'Based on traditional eating patterns of Mediterranean countries. Emphasizes olive oil, fish, vegetables, fruits, whole grains, and moderate wine consumption.'
    },
    {
      name: 'Nordic Diet',
      description: 'Traditional Scandinavian eating pattern emphasizing local foods like fish, root vegetables, berries, and whole grains. Sustainable and seasonal approach.'
    },
    {
      name: 'Asian Traditional Diets (e.g., Japanese Okinawan diet)',
      description: 'Various traditional Asian eating patterns emphasizing rice, vegetables, fish, and soy products. Often associated with longevity and low disease rates.'
    },
    {
      name: 'Mayan / Indigenous Food Diets',
      description: 'Traditional eating patterns of indigenous populations emphasizing native plants, minimal processing, and sustainable harvesting practices.'
    },
    {
      name: 'Ayurvedic Diet',
      description: 'Based on ancient Indian medicine principles, emphasizing foods that balance individual constitution (dosha) and seasonal eating patterns.'
    },
    {
      name: 'Traditional Chinese Medicine Diets',
      description: 'Emphasizes food as medicine, balancing hot and cold foods, and eating according to individual constitution and health conditions.'
    },
    {
      name: 'Kosher Diet',
      description: 'Follows Jewish dietary laws (kashrut) including restrictions on certain animals, preparation methods, and food combinations.'
    },
    {
      name: 'Halal Diet',
      description: 'Follows Islamic dietary guidelines, excluding pork and alcohol, and requiring specific slaughter methods for permissible animals.'
    },
    {
      name: 'Blue Zones-Inspired Diets',
      description: 'Based on eating patterns from regions with exceptional longevity. Emphasizes plant foods, moderate portions, and community dining.'
    }
  ],
  'High-Protein & Performance-Oriented Diets': [
    {
      name: 'Bodybuilding Diets',
      description: 'High-protein diets designed to support muscle growth and athletic performance. Often includes specific timing of nutrients around workouts.'
    },
    {
      name: 'High Protein / Low Fat',
      description: 'Emphasizes lean protein sources while minimizing fat intake. Often used for weight loss or muscle preservation during calorie restriction.'
    },
    {
      name: 'Zone Diet (macro balancing)',
      description: 'Maintains specific macronutrient ratios to optimize hormonal response and athletic performance. Emphasizes anti-inflammatory foods.'
    },
    {
      name: 'IIFYM (If It Fits Your Macros)',
      description: 'Flexible dieting approach that focuses on meeting daily macronutrient targets regardless of food sources. Emphasizes tracking over restriction.'
    },
    {
      name: 'Intermittent Fasting + High Protein',
      description: 'Combines time-restricted eating with high protein intake to preserve muscle mass during fasting periods and support recovery.'
    }
  ],
  'Fasting & Timing-Based Diets': [
    {
      name: 'Intermittent Fasting (16:8, 18:6, 20:4, OMAD)',
      description: 'Various time-restricted eating patterns that cycle between fasting and eating periods. May support weight loss and metabolic health.'
    },
    {
      name: 'Alternate-Day Fasting',
      description: 'Alternates between regular eating days and fasting or very low-calorie days. More intensive form of intermittent fasting.'
    },
    {
      name: '5:2 Diet',
      description: 'Normal eating five days per week with two non-consecutive days of very low calories (500-600). More moderate fasting approach.'
    },
    {
      name: 'Warrior Diet',
      description: 'Features a 20-hour fasting period with one large meal in the evening. Based on presumed ancient warrior eating patterns.'
    },
    {
      name: 'Time-Restricted Feeding',
      description: 'Limits daily eating to specific time windows, typically 8-12 hours. May help regulate circadian rhythms and metabolism.'
    },
    {
      name: 'Extended Water Fasting / Prolonged Fasts',
      description: 'Fasting periods lasting multiple days with only water consumption. Requires medical supervision and careful preparation.'
    },
    {
      name: 'Religious Fasting Diets (Ramadan, Daniel Fast, etc.)',
      description: 'Fasting practices based on religious traditions, often combining spiritual and health benefits with specific timing and food restrictions.'
    }
  ],
  'Anti-Inflammatory & Functional Diets': [
    {
      name: 'Anti-Inflammatory Diet (generalized)',
      description: 'Emphasizes foods that reduce inflammation while avoiding pro-inflammatory foods. Includes omega-3s, antioxidants, and colorful produce.'
    },
    {
      name: 'Autoimmune Protocol (AIP)',
      description: 'Elimination diet that removes potential inflammatory triggers to help manage autoimmune conditions. Very restrictive initial phase with gradual reintroduction.'
    },
    {
      name: 'Low Lectin Diet (e.g., Gundry\'s protocol)',
      description: 'Avoids foods high in lectins (plant proteins) that may contribute to inflammation and digestive issues according to some theories.'
    },
    {
      name: 'Low Histamine Diet',
      description: 'Reduces foods high in histamine or that trigger histamine release. Used to manage histamine intolerance and related symptoms.'
    },
    {
      name: 'Candida Diet',
      description: 'Eliminates sugar, refined carbs, and fermented foods to reduce yeast overgrowth. Controversial approach to managing candida-related health issues.'
    },
    {
      name: 'Detox / Cleanse Diets (Juice cleanses, Master Cleanse, etc.)',
      description: 'Short-term restrictive diets claiming to remove toxins from the body. Often involve liquid-only periods or specific food combinations.'
    },
    {
      name: 'Alkaline Diet',
      description: 'Based on the theory that certain foods affect body pH levels. Emphasizes alkaline-forming foods while avoiding acid-forming ones.'
    }
  ],
  'Specialized & Emerging Diets': [
    {
      name: 'Climate-Conscious / Planetary Health Diet',
      description: 'Focuses on foods with lower environmental impact, emphasizing plant foods and sustainable protein sources to address climate change.'
    },
    {
      name: 'Regenerative Agriculture / Local-Only Eating',
      description: 'Emphasizes foods produced through regenerative farming practices and local sourcing to support soil health and reduce transportation impact.'
    },
    {
      name: 'Biohacker Diets (Nootropic / longevity oriented)',
      description: 'Uses nutrition to optimize cognitive function, longevity, and performance. Often includes supplements and precise nutrient timing.'
    },
    {
      name: 'CR (Caloric Restriction) Diet',
      description: 'Reduces calorie intake while maintaining nutrition to potentially extend lifespan and improve health markers. Requires careful planning and monitoring.'
    },
    {
      name: 'Fasting-Mimicking Diet (e.g., ProLon)',
      description: 'Provides some nutrition while mimicking fasting benefits. Often involves specific low-calorie, low-protein meal plans for set periods.'
    },
    {
      name: 'Engine 2 Diet',
      description: 'Plant-based diet that eliminates processed foods, oils, and animal products. Developed by firefighters and emphasizes whole plant foods.'
    },
    {
      name: 'MIND Diet (for brain health)',
      description: 'Combines Mediterranean and DASH diets specifically for brain health. Emphasizes berries, leafy greens, and foods that support cognitive function.'
    },
    {
      name: 'Pegan (Paleo + Vegan hybrid)',
      description: 'Combines principles of Paleo and Vegan diets, emphasizing plants while including some high-quality animal products and avoiding grains and legumes.'
    },
    {
      name: 'Ornish Diet',
      description: 'Very low-fat, plant-based diet designed to reverse heart disease. Includes lifestyle modifications beyond diet such as stress management and exercise.'
    },
    {
      name: 'Optavia',
      description: 'Structured meal replacement program with coaching support. Uses proprietary products and gradual transition to self-prepared healthy meals.'
    }
  ]
};

// Diet Plan Management Component
function DietPlanManagement({ fetchWithAuth }) {
  const [dietPlans, setDietPlans] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedDietInfo, setSelectedDietInfo] = useState(null);

  useEffect(() => {
    loadDietPlans();
  }, []);

  const loadDietPlans = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/diet-plans/`);
      if (response.ok) {
        const data = await response.json();
        setDietPlans(data);
        // Set selected plans from saved data
        const activePlans = data.filter(plan => plan.is_active).map(plan => plan.diet_name);
        setSelectedPlans(activePlans);
      }
    } catch (error) {
      console.error('Failed to load diet plans:', error);
    }
  };

  const handleSaveDietPlans = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/diet-plans/bulk-update`, {
        method: 'POST',
        body: JSON.stringify({
          selected_diets: selectedPlans
        })
      });
      
      if (response.ok) {
        await loadDietPlans();
        setShowEditModal(false);
        alert('Diet plans updated successfully!');
      } else {
        alert('Failed to update diet plans. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save diet plans:', error);
      alert('Failed to save diet plans. Please try again.');
    }
  };

  const handleDietToggle = (dietName) => {
    setSelectedPlans(prev => {
      if (prev.includes(dietName)) {
        return prev.filter(name => name !== dietName);
      } else if (prev.length < 10) {
        return [...prev, dietName];
      } else {
        alert('You can select up to 10 diet plans maximum.');
        return prev;
      }
    });
  };

  const showDietInfo = (diet) => {
    setSelectedDietInfo(diet);
    setShowInfoModal(true);
  };

  const getActivePlansDisplay = () => {
    const activePlans = dietPlans.filter(plan => plan.is_active);
    if (activePlans.length === 0) {
      return 'No diet plans selected';
    }
    return activePlans.map(plan => plan.diet_name).join(', ');
  };

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h3 className="card-title">Diet Plan Management</h3>
        <button 
          className="btn btn-primary btn-sm"
          onClick={() => setShowEditModal(true)}
        >
          Edit Plans
        </button>
      </div>
      
      <div className="diet-plans-display">
        <p><strong>Current Active Plans:</strong> {getActivePlansDisplay()}</p>
        <p className="dpm-sectionHint">
          {dietPlans.filter(plan => plan.is_active).length} of 10 maximum plans selected
        </p>
      </div>

      {/* Edit Diet Plans Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content diet-modal">
            <div className="modal-header">
              <h3>Select Diet Plans</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            
            <div className="diet-modal-instructions">
              <p>Select up to 10 Diet Plans to include. Keep in mind the more plans you select the more limited your Nutrition Agent will be in recommending foods, recipes, meals, or plans. Also be mindful not to select Diet Plans that contain mutually exclusive foods (e.g. Vegan and Paleo - where Vegan is almost entirely plant-based and Paleo is heavily meat-based).</p>
              <p><strong>Selected: {selectedPlans.length}/10</strong></p>
            </div>

            <div className="diet-categories-container">
              {Object.entries(DIET_CATEGORIES).map(([category, diets]) => (
                <div key={category} className="diet-category">
                  <h4 className="diet-category-header">{category}</h4>
                  {diets.map(diet => (
                  <div key={diet.name} className="diet-row dpm-dietRow">                      
                     <div className="diet-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPlans.includes(diet.name)}
                          onChange={() => handleDietToggle(diet.name)}
                          disabled={!selectedPlans.includes(diet.name) && selectedPlans.length >= 10}
                        />
                      </div>
                      <div className="diet-name">
                        {diet.name}
                      </div>
                      <div className="diet-info">
                        <button 
                          className="info-button"
                          onClick={() => showDietInfo(diet)}
                          title="View diet information"
                        >
                          i
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveDietPlans}
              >
                Save Diet Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Diet Information Modal */}
      {showInfoModal && selectedDietInfo && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{selectedDietInfo.name}</h3>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>&times;</button>
            </div>
            
            <div className="diet-info-content">
              <p>{selectedDietInfo.description}</p>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-primary" 
                onClick={() => setShowInfoModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DietPlanManagement;